/**
 * Customer Selection Toolbar Component
 * Displays when customer accounts are selected and provides bulk actions
 * Matches the styling and behavior of the Transactions SelectionToolbar
 */

import {
  ChevronDown,
  Download,
  FileSpreadsheet,
  Mail,
  Phone,
  StickyNote,
  Target,
  TrendingUp,
  UserCheck,
  UserCog,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { RiskLevelCode } from '@/lib/risk-level';
import { formatCurrency } from '@/lib/utils';

interface Customer {
  id: string;
  name: string;
  accountNumber: string;
  currency: 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD';
  outstanding: number;
  overdue: number;
  outstandingHome?: number;
  overdueHome?: number;
  displayOutstanding?: number;
  displayOverdue?: number;
  isAnyCurrency?: boolean;
  creditLimit: number;
  dso: number;
  risk: RiskLevelCode;
  lastPayment: string;
  contact: string;
  phone: string;
  address: string;
  paymentTerms: string;
  collectionStrategyId: string;
}

interface CustomerSelectionToolbarProps {
  selectedCount: number;
  selectedCustomers: Customer[];
  baseCurrency?: string;
  onClearSelection: () => void;
  onSendEmail: () => void;
  onLogCall: () => void;
  onSendStatements: () => void;
  onAssignStrategy: () => void;
  onAssignCreditController: () => void;
  onAssignAccountManager: () => void;
  onExportToExcel: () => void;
  onExportAllColumns: () => void;
  onAddNote?: () => void;
  onAssignRiskLevel?: () => void;
}

export function CustomerSelectionToolbar({
  selectedCount,
  selectedCustomers,
  baseCurrency = 'GBP',
  onClearSelection,
  onSendEmail,
  onLogCall,
  onSendStatements,
  onAssignStrategy,
  onAssignCreditController,
  onAssignAccountManager,
  onExportToExcel,
  onExportAllColumns,
  onAddNote,
  onAssignRiskLevel,
}: Readonly<CustomerSelectionToolbarProps>) {
  // Determine whether all selected customers share the same currency
  // and none are flagged as "any currency" (multi-currency accounts)
  const hasAnyCurrencyCustomer = selectedCustomers.some((c) => c.isAnyCurrency);
  const allSameCurrency =
    !hasAnyCurrencyCustomer &&
    selectedCustomers.length > 0 &&
    selectedCustomers.every((c) => c.currency === selectedCustomers[0].currency);

  // When all customers share one currency, sum display values in that currency;
  // otherwise fall back to home/base values
  const useHomeCurrency = !allSameCurrency;

  const totalOutstanding = selectedCustomers.reduce(
    (sum, c) =>
      sum + (useHomeCurrency ? (c.outstandingHome ?? c.outstanding) : (c.displayOutstanding ?? c.outstanding)),
    0,
  );
  const totalOverdue = selectedCustomers.reduce(
    (sum, c) => sum + (useHomeCurrency ? (c.overdueHome ?? c.overdue) : (c.displayOverdue ?? c.overdue)),
    0,
  );

  const displayCurrency = useHomeCurrency ? baseCurrency : selectedCustomers[0]?.currency || baseCurrency;

  // Check if only one customer is selected for single-customer actions
  const isSingleCustomer = selectedCount === 1;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 duration-300">
      <div className="bg-primary text-primary-foreground rounded-lg shadow-2xl border border-primary/20">
        <div className="flex items-center gap-4 px-6 py-4">
          {/* Selection Info */}
          <div className="flex items-center gap-3 border-r border-primary-foreground/20 pr-4">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <Badge
                  variant="secondary"
                  className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30"
                >
                  {selectedCount}
                </Badge>
                <span className="font-medium">{selectedCount === 1 ? 'customer' : 'customers'} selected</span>
              </div>
              <div className="flex items-center gap-3 mt-1 text-sm text-primary-foreground/80">
                <span>
                  Outstanding:{' '}
                  <span className="font-semibold">{formatCurrency(totalOutstanding, displayCurrency)}</span>
                </span>
                {totalOverdue > 0 && (
                  <>
                    <span>•</span>
                    <span>
                      Overdue: <span className="font-semibold">{formatCurrency(totalOverdue, displayCurrency)}</span>
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="sm" className="gap-2">
                  <FileSpreadsheet className="h-4 w-4" />
                  Export
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onExportToExcel}>Export Current View</DropdownMenuItem>
                <DropdownMenuItem onClick={onExportAllColumns}>Export All Columns</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="sm" className="gap-2">
                  Actions
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {isSingleCustomer && (
                  <>
                    <DropdownMenuItem onClick={onAddNote} className="gap-2">
                      <StickyNote className="h-4 w-4" />
                      Add Note
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={onSendEmail} className="gap-2">
                  <Mail className="h-4 w-4" />
                  Send Email ({selectedCount})
                </DropdownMenuItem>
                {isSingleCustomer && (
                  <DropdownMenuItem onClick={onLogCall} className="gap-2">
                    <Phone className="h-4 w-4" />
                    Log Call
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onAssignStrategy} className="gap-2">
                  <Target className="h-4 w-4" />
                  Assign Collection Strategy
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onAssignRiskLevel} className="gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Assign Risk Level
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onAssignCreditController} className="gap-2">
                  <UserCog className="h-4 w-4" />
                  Assign Credit Controller
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onAssignAccountManager} className="gap-2">
                  <UserCheck className="h-4 w-4" />
                  Assign Account Manager
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onSendStatements} className="gap-2">
                  <Download className="h-4 w-4" />
                  Send Statements ({selectedCount})
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="ghost"
              size="sm"
              onClick={onClearSelection}
              className="gap-2 hover:bg-primary-foreground/20"
            >
              <X className="h-4 w-4" />
              Clear
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
